import React, { useEffect, useMemo, useState } from "react"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { api } from "@/lib/api"
import { ImpersonationTarget } from "@/stores/impersonationStore"
import { useImpersonation } from "@/lib/useImpersonation"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Full-screen picker for "view as user", mirroring OrganizationSelector so the
 * two identity switchers in the app feel like the same gesture.
 *
 * Filtering is a plain input rather than a combobox on purpose: there is no
 * `command` component in the design system and cmdk is not a dependency, and
 * this list is small enough that adding one would not earn its weight.
 */
export function ImpersonationSelector({ open, onOpenChange }: Props) {
  const [targets, setTargets] = useState<ImpersonationTarget[]>([])
  const [query, setQuery] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const { startImpersonation } = useImpersonation()

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .listImpersonationTargets()
      .then((rows) => {
        if (!cancelled) setTargets(rows)
      })
      .catch((e) => {
        console.error("Failed to load impersonation targets:", e)
        if (!cancelled) setError("Could not load users.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return targets
    return targets.filter(
      (t) =>
        t.email.toLowerCase().includes(q) ||
        (t.name || "").toLowerCase().includes(q) ||
        t.organizations.some((o) => o.name.toLowerCase().includes(q))
    )
  }, [targets, query])

  if (!open) return null

  const handlePick = async (target: ImpersonationTarget) => {
    setPendingId(target.id)
    try {
      await startImpersonation(target)
      onOpenChange(false)
      setQuery("")
    } catch (e) {
      console.error("Failed to start impersonation:", e)
      setError("Could not view as that user.")
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-background">
      <div className="container mx-auto flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col px-4 py-6 sm:px-6 sm:py-10">
        <div className="flex shrink-0 flex-col items-stretch gap-4 border-b pb-4 sm:pb-6">
          <div>
            <h2 className="text-xl font-bold sm:text-2xl">View as user</h2>
            <p className="text-sm text-muted-foreground">
              See the app exactly as they do. The session is read-only and is
              recorded in the audit log.
            </p>
          </div>
          <Input
            autoFocus
            placeholder="Filter by name, email, or organization…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-4 sm:py-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading users…</p>
          ) : error ? (
            <Alert variant="destructive">
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : filtered.length === 0 ? (
            <Alert>
              <AlertTitle>No matching users</AlertTitle>
              <AlertDescription>
                Superusers are not listed — viewing as one grants nothing and
                only muddies the audit trail.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid w-full max-w-2xl gap-3">
              {filtered.map((t) => (
                <Card
                  key={t.id}
                  className="cursor-pointer p-4 transition-colors hover:bg-muted"
                  onClick={() => pendingId === null && handlePick(t)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold">
                        {t.name || t.email}
                      </h3>
                      {t.name ? (
                        <p className="truncate text-sm text-muted-foreground">
                          {t.email}
                        </p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {t.organizations.length === 0 ? (
                          <Badge variant="outline">No organizations</Badge>
                        ) : (
                          t.organizations.map((o) => (
                            <Badge key={o.id} variant="secondary">
                              {o.name} · {o.role}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                    {pendingId === t.id ? (
                      <span className="shrink-0 text-sm text-muted-foreground">
                        Switching…
                      </span>
                    ) : null}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t pt-4 sm:pt-6">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ImpersonationSelector
